import { useEffect, useState, type ReactNode } from 'react';
import { useLogDeliverable } from '../contexts/LogDeliverableContext.js';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.js';
import { routes } from '../routes.js';
import { usePWAInstall } from '../hooks/usePWAInstall.js';
import { ScopeWordmark as SharedScopeWordmark } from '../components/ScopeWordmark.js';
import { trpc } from '../lib/trpc.js';
import { HelpDrawer } from '../components/HelpDrawer.js';
import { AgencyHelpContent } from '../components/agency/AgencyHelpContent.js';
import { CommandPalette } from '../components/agency/CommandPalette.js';
import { useDisplayName } from '../hooks/useDisplayName.js';
import { NotificationOptInBanner } from '../components/NotificationOptInBanner.js';

/**
 * Agency-side application shell. Sidebar nav + topbar + main content area.
 * Layout matches Scope_UI_Mockup.html screen 1.
 *
 * Phase 3 agents may ADD nav items to the NAV_ITEMS array (alphabetical-by-label)
 * but must not change the shell structure.
 *
 * Phase 5 additions: hamburger drawer on < md (768px), install-app button in topbar.
 */

interface NavItem {
  label: string;
  to: string;
  icon: ReactNode;
  badge?: ReactNode;
}

// Phase 3 agents append here as needed. Keep alphabetical by label after Dashboard.
const NAV_ITEMS: NavItem[] = [
  {
    label: 'Dashboard',
    to: routes.dashboard,
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="3" width="7" height="9" />
        <rect x="14" y="3" width="7" height="5" />
        <rect x="14" y="12" width="7" height="9" />
        <rect x="3" y="16" width="7" height="5" />
      </svg>
    ),
  },
  {
    label: 'Clients',
    to: routes.clients,
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4" />
      </svg>
    ),
  },
  {
    label: 'Engagements',
    to: routes.engagements,
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="2" y="7" width="20" height="14" rx="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      </svg>
    ),
  },
  {
    label: 'Projects',
    to: routes.projects,
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 4h6l2 2h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
        <path d="M2 10h20" />
      </svg>
    ),
  },
  {
    label: 'Deliverables',
    to: routes.deliverables,
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="m7.5 4.27 9 5.15" />
        <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
        <path d="m3.3 7 8.7 5 8.7-5" />
        <path d="M12 22V12" />
      </svg>
    ),
  },
  {
    label: 'Reports',
    to: routes.reports,
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    ),
  },
  {
    label: 'Documents',
    to: routes.documents,
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
    ),
  },
  {
    label: 'Invoices',
    to: routes.invoices,
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
  },
  {
    label: 'Inbox',
    to: routes.inbox,
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
        <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
      </svg>
    ),
  },
  {
    label: 'Notifications',
    to: routes.notifications,
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
  },
  {
    label: 'Activity log',
    to: routes.auditLog,
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 8v4l3 3" />
        <circle cx="12" cy="12" r="10" />
      </svg>
    ),
  },
];

function SidebarContent({
  auth,
  onNavClick,
}: {
  auth: { email: string };
  onNavClick?: () => void;
}) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const unreadCountQuery = trpc.notification.unreadCount.useQuery(undefined, {
    refetchInterval: 60_000,
  });
  const unread = unreadCountQuery.data?.count ?? 0;
  const inboxCountsQuery = trpc.promptQueue.counts.useQuery(undefined, {
    refetchInterval: 60_000,
  });
  const activePrompts = inboxCountsQuery.data?.active ?? 0;
  const [helpOpen, setHelpOpen] = useState(false);
  const { name: displayName, initials } = useDisplayName();

  return (
    <>
      <div className="px-6 pb-8 pt-7">
        <ScopeWordmark />
      </div>

      <nav className="flex-1 px-3">
        {NAV_ITEMS.map((item) => {
          const badge =
            item.label === 'Notifications' && unread > 0 ? (
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-status-blue px-1.5 text-2xs font-semibold text-white">
                {unread > 99 ? '99+' : unread}
              </span>
            ) : item.label === 'Inbox' && activePrompts > 0 ? (
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-status-amber px-1.5 text-2xs font-semibold text-white">
                {activePrompts > 99 ? '99+' : activePrompts}
              </span>
            ) : (
              item.badge
            );
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === routes.dashboard}
              onClick={onNavClick}
              className={({ isActive }) =>
                [
                  'mb-1 flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition',
                  isActive
                    ? 'bg-white font-medium text-scope-navy'
                    : 'text-scope-cream/80 hover:bg-scope-navy-soft',
                ].join(' ')
              }
            >
              {item.icon}
              {item.label}
              {badge && <span className="ml-auto">{badge}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* Settings + Users + user card */}
      <div className="mx-3 border-t border-scope-navy-soft px-3 pb-6 pt-4">
        <NavLink
          to={routes.users}
          onClick={onNavClick}
          className={({ isActive }) =>
            [
              'mb-1 flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition',
              isActive
                ? 'bg-white font-medium text-scope-navy'
                : 'text-scope-cream/80 hover:bg-scope-navy-soft',
            ].join(' ')
          }
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          Users
        </NavLink>
        <NavLink
          to={routes.projectTemplates}
          onClick={onNavClick}
          className={({ isActive }) =>
            [
              'mb-1 flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition',
              isActive
                ? 'bg-white font-medium text-scope-navy'
                : 'text-scope-cream/80 hover:bg-scope-navy-soft',
            ].join(' ')
          }
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
          Templates
        </NavLink>
        <NavLink
          to={routes.agencySettings}
          onClick={onNavClick}
          className={({ isActive }) =>
            [
              'flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition',
              isActive
                ? 'bg-white font-medium text-scope-navy'
                : 'text-scope-cream/80 hover:bg-scope-navy-soft',
            ].join(' ')
          }
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
          Settings
        </NavLink>
        <button
          onClick={() => setHelpOpen(true)}
          className="flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-scope-cream/80 transition hover:bg-scope-navy-soft"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          Help
        </button>
        <button
          onClick={async () => {
            await signOut();
            navigate(routes.login);
          }}
          className="mt-3 flex min-h-[44px] items-center gap-3 px-3 text-xs text-scope-cream/60 hover:text-scope-cream"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-scope-green text-xs font-semibold text-white">
            {initials}
          </div>
          <div className="min-w-0 text-left">
            <div className="truncate font-medium text-scope-cream">{displayName}</div>
            <div className="truncate text-scope-cream/50">{auth.email} · Sign out</div>
          </div>
        </button>
      </div>

      <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} title="Help">
        <AgencyHelpContent />
      </HelpDrawer>
    </>
  );
}

export function AgencyShell() {
  const { auth } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Cmd/Ctrl+K to open the command palette anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  if (auth.kind !== 'agency') {
    return null;
  }

  return (
    <div className="flex min-h-screen bg-canvas font-sans">
      {/* Desktop sidebar — hidden below md (768px) */}
      <aside className="hidden w-60 shrink-0 flex-col bg-scope-navy md:flex">
        <SidebarContent auth={auth} />
      </aside>

      {/* Mobile drawer backdrop */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}
      {/* Mobile drawer sidebar */}
      <aside
        className={[
          'fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-scope-navy transition-transform duration-300 md:hidden',
          drawerOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
        aria-label="Navigation menu"
      >
        <SidebarContent auth={auth} onNavClick={() => setDrawerOpen(false)} />
      </aside>

      {/* Main content area */}
      <main className="min-w-0 flex-1 overflow-auto">
        <AgencyTopBar
          onMenuClick={() => setDrawerOpen(true)}
          onSearchClick={() => setPaletteOpen(true)}
        />
        <div className="px-4 pt-4 md:px-8">
          <NotificationOptInBanner auth={auth} variant="light" />
        </div>
        <Outlet />
      </main>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}

function AgencyTopBar({
  onMenuClick,
  onSearchClick,
}: {
  onMenuClick: () => void;
  onSearchClick: () => void;
}) {
  const { open } = useLogDeliverable();
  const { canInstall, triggerInstall } = usePWAInstall();

  return (
    <div className="sticky top-0 z-30 flex h-14 items-center justify-between gap-2 border-b border-scope-border bg-white px-4 md:justify-end md:px-8">
      {/* Hamburger button — mobile only */}
      <button
        onClick={onMenuClick}
        className="flex h-11 w-11 items-center justify-center rounded-lg text-scope-navy hover:bg-scope-cream-warm md:hidden"
        aria-label="Open navigation menu"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>

      <div className="flex items-center gap-2">
        {/* Search button (opens Cmd+K palette) */}
        <button
          onClick={onSearchClick}
          aria-label="Search"
          className="inline-flex h-11 items-center gap-2 rounded-lg border border-scope-border bg-white px-3 text-xs text-scope-text-muted hover:border-scope-navy/30 hover:text-scope-navy transition"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <span className="hidden sm:inline">Search</span>
          <kbd className="hidden rounded border border-scope-border bg-scope-cream-warm px-1 text-2xs font-mono sm:inline">
            ⌘K
          </kbd>
        </button>
        {/* Install app button — shown when browser allows PWA install */}
        {canInstall && (
          <button
            onClick={() => void triggerInstall()}
            className="inline-flex h-11 items-center gap-1.5 rounded-lg border border-scope-border px-3 text-xs font-medium text-scope-navy hover:bg-scope-cream-warm transition"
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Install app
          </button>
        )}
        <button
          onClick={open}
          className="inline-flex h-11 items-center gap-2 rounded-lg bg-scope-navy px-3.5 text-sm font-medium text-white hover:bg-scope-navy-soft transition"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          Log deliverable
        </button>
      </div>
    </div>
  );
}

function ScopeWordmark() {
  return <SharedScopeWordmark tone="dark" className="h-7 w-auto" />;
}
